import React, { useState } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import {
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import DueDateLegend from './DueDateLegend';

const locales = {
  'en-US': require('date-fns/locale/en-US')
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales
});

// Helper function to get due date status color
const getDueDateStatusColor = (dueDate) => {
  if (!dueDate) return '#6B7280'; // gray-500
  
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Parse the date and set to noon to avoid timezone issues
    const dueDateObj = parseTaskDate(dueDate);
    
    const diffTime = dueDateObj.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return '#EF4444'; // red-500 - Overdue
    } else if (diffDays <= 7) {
      return '#F97316'; // orange-500 - Due soon
    } else {
      return '#22C55E'; // green-500 - Due later
    }
  } catch (e) {
    console.warn('Error calculating due date status:', e);
    return '#6B7280'; // gray-500
  }
};

// Helper function to parse dates from the server consistently
const parseTaskDate = (dateString) => {
  if (!dateString) return null;
  
  try {
    // Split the date string to get components
    const parts = dateString.split('T')[0].split('-');
    if (parts.length !== 3) return new Date(dateString);
    
    // Create date with noon time to avoid timezone shifting
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // JS months are 0-indexed
    const day = parseInt(parts[2], 10);
    
    return new Date(year, month, day, 12, 0, 0);
  } catch (e) {
    console.warn('Error parsing task date:', e);
    return new Date(dateString);
  }
};

// Get initials from a user's name
const getInitials = (name) => {
  if (!name) return '';
  
  const names = name.split(' ');
  if (names.length === 1) return names[0].charAt(0).toUpperCase();
  
  return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
};

// User colors for consistent color coding
const userColors = {};
const getColorForUser = (userId) => {
  if (userColors[userId]) return userColors[userId];
  
  // Generate a consistent color based on the userId
  const colorOptions = [
    '#3B82F6', // blue-500
    '#8B5CF6', // violet-500
    '#EC4899', // pink-500
    '#14B8A6', // teal-500
    '#F59E0B', // amber-500
    '#6366F1', // indigo-500
    '#10B981', // emerald-500
    '#06B6D4', // cyan-500
    '#A855F7', // purple-500
    '#F43F5E', // rose-500
  ];
  
  // Deterministic selection based on userId
  const index = parseInt(userId) % colorOptions.length;
  userColors[userId] = colorOptions[index];
  
  return userColors[userId];
};

const TaskCalendar = ({ tasks, onDelete, onEdit, onToggleComplete, onView, users }) => {
  const [view, setView] = useState('month');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetailsPos, setEventDetailsPos] = useState({ top: 0, left: 0 });
  
  // Find user name by ID
  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? user.name : 'Unassigned';
  };
  
  // Convert tasks to calendar events
  const calendarEvents = tasks
    .filter(task => task.due_date) // Only include tasks with due dates
    .map(task => {
      // Determine color based on user assignment or completion status
      const baseColor = task.assigned_user_id 
        ? getColorForUser(task.assigned_user_id)
        : task.is_completed
          ? '#10B981' // green for completed
          : '#6B7280'; // gray for unassigned
          
      // Get assigned user name
      const userName = task.assigned_user_id 
        ? getUserName(task.assigned_user_id)
        : 'Unassigned';
        
      // Get initials to display at the beginning of the task title
      const initialsDisplay = task.assigned_user_id
        ? `[${getInitials(userName)}] `
        : '[?] ';

      return {
        id: task.id,
        title: `${initialsDisplay}${task.name}`,
        start: parseTaskDate(task.due_date),
        end: parseTaskDate(task.due_date),
        allDay: true,
        resource: {
          task,
          dueDateColor: getDueDateStatusColor(task.due_date),
          baseColor
        }
      };
    });
  
  // Style event based on due date status and user
  const eventStyleGetter = (event) => {
    const resource = event.resource || {};
    const baseColor = resource.baseColor;
    const dueDateColor = resource.dueDateColor;
    const isCompleted = resource.task?.is_completed;
    
    // Custom style for completed items (opacity and strikethrough)
    const opacity = isCompleted ? '0.6' : '1';
    const textDecoration = isCompleted ? 'line-through' : 'none';
    
    return {
      style: {
        backgroundColor: baseColor,
        borderLeft: `4px solid ${dueDateColor}`,
        color: 'white',
        borderRadius: '4px',
        display: 'block',
        padding: '2px 5px',
        opacity,
        textDecoration
      }
    };
  };
  
  // Handle click on event to show details
  const handleEventClick = (event, e) => {
    setSelectedEvent(event);
    
    // Position the details popup in the center of the calendar container
    // instead of relative to the clicked element
    const calendarContainer = document.querySelector('.rbc-calendar');
    if (calendarContainer) {
      const rect = calendarContainer.getBoundingClientRect();
      setEventDetailsPos({
        top: rect.top + rect.height / 2 - 150, // position 150px above center (half the approximate height of modal)
        left: rect.left + rect.width / 2 - 200 // position 200px to the left of center (half the approximate width of modal)
      });
    } else {
      // Fallback to center of viewport if calendar container not found
      setEventDetailsPos({
        top: window.innerHeight / 2 - 150,
        left: window.innerWidth / 2 - 200
      });
    }
  };
  
  // Close event details
  const handleCloseDetails = () => {
    setSelectedEvent(null);
  };
  
  // Event Details Popup
  const EventDetailsPopup = ({ event, position }) => {
    // State for delete confirmation - moved outside conditionals to follow React Hooks rules
    const [showDeleteOptions, setShowDeleteOptions] = useState(false);
    const [deleteEntireSeries, setDeleteEntireSeries] = useState(false);
    const [deleteFutureTasks, setDeleteFutureTasks] = useState(false);
    
    if (!event) return null;
    
    const task = event.resource.task;
    const dueDateColor = event.resource.dueDateColor;
    
    // Handle delete task
    const handleDeleteTask = () => {
      if (task.is_recurring && task.recurring_series_id && !showDeleteOptions) {
        // Show delete options for recurring tasks
        setShowDeleteOptions(true);
      } else {
        // Delete the task with selected options
        onDelete(task.id, deleteEntireSeries, deleteFutureTasks);
        handleCloseDetails();
      }
    };
    
    return (
      <>
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={handleCloseDetails}
        ></div>
        
        {/* Modal */}
        <div 
          className="fixed bg-gray-800 rounded-lg shadow-xl border border-gray-700 p-4 max-w-md z-50 transform -translate-x-1/2 -translate-y-1/2"
          style={{ 
            top: '50%',
            left: '50%',
            maxWidth: '400px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}
        >
          <div className="flex justify-between items-start">
            <h3 className="text-lg font-medium text-white pr-8">{task.name}</h3>
            <button 
              onClick={handleCloseDetails} 
              className="text-gray-400 hover:text-gray-300"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          
          <div className="mt-2 space-y-3">
            {task.description && (
              <p className="text-sm text-gray-300">{task.description}</p>
            )}
            
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-400">Due:</span>
              <span 
                className="text-sm font-medium" 
                style={{ color: dueDateColor }}
              >
                {format(parseTaskDate(task.due_date), 'MMM d, yyyy')}
              </span>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-400">Assigned to:</span>
              <span className="text-sm text-gray-200">
                {task.assigned_user_id 
                  ? getUserName(task.assigned_user_id)
                  : 'Unassigned'
                }
              </span>
            </div>
            
            {task.is_recurring && (
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-400">Recurs:</span>
                <span className="text-sm text-purple-400">{task.recurrence_pattern}</span>
              </div>
            )}
            
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-400">Status:</span>
              <span 
                className={`text-sm ${task.is_completed ? 'text-green-400' : 'text-blue-400'}`}
              >
                {task.is_completed ? 'Completed' : 'Active'}
              </span>
            </div>
            
            {/* Delete Options for Recurring Tasks */}
            {showDeleteOptions && task.is_recurring && task.recurring_series_id && (
              <div className="pt-2 border-t border-gray-700 space-y-2">
                <p className="text-sm font-medium text-gray-300">
                  This is a recurring task. What would you like to delete?
                </p>
                
                <label className="flex items-center">
                  <input
                    type="radio"
                    className="form-radio h-4 w-4 text-indigo-600 rounded-full bg-gray-700 border-gray-600"
                    name="delete-option"
                    checked={!deleteEntireSeries && !deleteFutureTasks}
                    onChange={() => {
                      setDeleteEntireSeries(false);
                      setDeleteFutureTasks(false);
                    }}
                  />
                  <span className="ml-2 text-sm text-gray-300">
                    Only this task
                  </span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="radio"
                    className="form-radio h-4 w-4 text-indigo-600 rounded-full bg-gray-700 border-gray-600"
                    name="delete-option"
                    checked={deleteFutureTasks}
                    onChange={() => {
                      setDeleteEntireSeries(false);
                      setDeleteFutureTasks(true);
                    }}
                  />
                  <span className="ml-2 text-sm text-gray-300">
                    This task and all future tasks
                  </span>
                </label>
              </div>
            )}
            
            <div className="pt-2 flex justify-end space-x-2 border-t border-gray-700">
              <button
                onClick={() => {
                  onToggleComplete(task.id, !task.is_completed);
                  handleCloseDetails();
                }}
                className={`p-1 rounded-full ${
                  task.is_completed 
                    ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700' 
                    : 'text-green-400 hover:text-green-300 hover:bg-green-900/25'
                }`}
                title={task.is_completed ? 'Mark as incomplete' : 'Mark as complete'}
              >
                {task.is_completed ? (
                  <XMarkIcon className="h-5 w-5" />
                ) : (
                  <CheckIcon className="h-5 w-5" />
                )}
              </button>
              
              <button
                onClick={() => {
                  onView(task);
                  handleCloseDetails();
                }}
                className="p-1 rounded-full text-gray-400 hover:text-gray-300 hover:bg-gray-700"
                title="View task details"
              >
                <EyeIcon className="h-5 w-5" />
              </button>
              
              <button
                onClick={() => {
                  onEdit(task);
                  handleCloseDetails();
                }}
                className="p-1 rounded-full text-blue-400 hover:text-blue-300 hover:bg-blue-900/25"
                title="Edit task"
              >
                <PencilIcon className="h-5 w-5" />
              </button>
              
              <button
                onClick={handleDeleteTask}
                className="p-1 rounded-full text-red-400 hover:text-red-300 hover:bg-red-900/25"
                title="Delete task"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="bg-gray-800 p-4 rounded-md shadow-sm border border-gray-700 h-[700px]">
      {/* Legend - Using reusable component */}
      <DueDateLegend viewType="calendar" />
      
      <Calendar
        localizer={localizer}
        events={calendarEvents}
        startAccessor="start"
        endAccessor="end"
        style={{ height: '100%' }}
        views={['month', 'week', 'day']}
        view={view}
        onView={setView}
        eventPropGetter={eventStyleGetter}
        onSelectEvent={handleEventClick}
        popup
        className="dark-calendar"
      />
      
      {/* Event Details Popup */}
      {selectedEvent && (
        <EventDetailsPopup event={selectedEvent} position={eventDetailsPos} />
      )}
    </div>
  );
};

export default TaskCalendar; 